import { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { isActiveYear } from '../config/academicYear';
import SearchableDropdown from './SearchableDropdown';
import './OpticalReader.css';

const ANSWER_START_COL = 12;

const TR_MAP = {'İ':'I','ı':'i','Ş':'S','ş':'s','Ğ':'G','ğ':'g','Ü':'U','ü':'u','Ö':'O','ö':'o','Ç':'C','ç':'c'};
const normalizeTr = (s) => s.replace(/[İıŞşĞğÜüÖöÇç]/g, c => TR_MAP[c] || c);

const colName = (i) => {
  let name = '';
  do {
    name = String.fromCharCode(65 + (i % 26)) + name;
    i = Math.floor(i / 26) - 1;
  } while (i >= 0);
  return name;
};

export default function OpticalReader() {
  const [activeYear, setActiveYear] = useState(isActiveYear);
  const archiveReadOnly = !activeYear;
  const [headers, setHeaders]               = useState([]);
  const [rows, setRows]                     = useState([]);
  const [answerKeys, setAnswerKeys]         = useState({});
  const [classicPoints, setClassicPoints]   = useState({});
  const [numClassicQuestions, setNumClassicQuestions] = useState({});
  const [scoringMode, setScoringMode]       = useState('equal');
  const [mcManualPoints, setMcManualPoints] = useState({});
  const [manualActiveGroup, setManualActiveGroup] = useState('');
  const [fileName, setFileName]             = useState('');
  const [csvContent, setCsvContent]         = useState('');
  const [error, setError]                   = useState('');
  const [dragging, setDragging]             = useState(false);
  const [activeTab, setActiveTab]           = useState('key');
  const inputRef = useRef(null);

  // Ders seçimi
  const [courses, setCourses]               = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseError, setCourseError]         = useState('');

  const [courseExams, setCourseExams]        = useState([]);
  const [loadingExams, setLoadingExams]      = useState(false);
  const [selectedExamId, setSelectedExamId]  = useState('');
  const [saving, setSaving]                 = useState(false);
  const [saveMsg, setSaveMsg]               = useState('');
  const [savedSinavId, setSavedSinavId]     = useState(null);
  const [bookletOrders, setBookletOrders]   = useState({});

  // Öğrenci no -> ad soyad haritası (seçili derse göre)
  const [studentNames, setStudentNames] = useState({});

  // Önceki optikler
  const [previousSinavlar, setPreviousSinavlar] = useState([]);
  const [loadingPrevious, setLoadingPrevious]   = useState(false);
  const [confirmDeleteId, setConfirmDeleteId]   = useState(null);
  const [deleting, setDeleting]                 = useState(false);
  const [manualBookletSet, setManualBookletSet]  = useState(new Set());
  const [savedOpticResults, setSavedOpticResults] = useState({});
  const [loadedExamType, setLoadedExamType] = useState('');

  useEffect(() => {
    const updateYear = () => setActiveYear(isActiveYear());
    window.addEventListener('academic-year-meta-changed', updateYear);
    return () => window.removeEventListener('academic-year-meta-changed', updateYear);
  }, []);

  useEffect(() => {
    const akademisyenId = sessionStorage.getItem('akademisyen_id');
    if (!akademisyenId) {
      setCourseError('Oturum bilgisi bulunamadı. Lütfen yeniden giriş yapın.');
      return;
    }

    setLoadingCourses(true);
    setCourseError('');
    axios.get(API_ENDPOINTS.AKADEMISYEN_COURSES_DETAILED, {
      params: { akademisyen_id: akademisyenId },
    })
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.courses;
        if (!Array.isArray(raw)) throw new Error('Ders listesi alınamadı.');

        const normalized = raw.map(c => ({
          courseId:        String(c.courseId ?? c.course_id ?? ''),
          programId:       String(c.programId ?? c.program_id ?? ''),
          programName:     c.programName ?? c.program_name ?? '',
          courseCode:      c.courseCode ?? c.course_code ?? '',
          courseName:      c.courseName ?? c.course_name ?? '',
          facultyId:       String(c.facultyId ?? c.faculty_id ?? ''),
          facultyName:     c.facultyName ?? c.faculty_name ?? '',
          educationTypeId: c.educationTypeId ?? c.education_type_id ?? '',
          hasOptic:        Boolean(c.hasOptic ?? c.has_optic),
        }));
        setCourses(normalized.filter(c => c.courseId));
      })
      .catch(() => {
        setCourses([]);
        setCourseError('Dersler yüklenemedi. Sayfayı yenileyip tekrar deneyin.');
      })
      .finally(() => setLoadingCourses(false));
  }, []);


  const selectedCourse = useMemo(
    () => courses.find(c => c.courseId === selectedCourseId) || null,
    [courses, selectedCourseId]
  );
  const selectedExam = useMemo(
    () => courseExams.find(e => String(e.id) === selectedExamId) || null,
    [courseExams, selectedExamId]
  );

  useEffect(() => {
    setCourseExams([]);
    setSelectedExamId('');
    setBookletOrders({});
    setHeaders([]);
    setRows([]);
    setAnswerKeys({});
    setClassicPoints({});
    setNumClassicQuestions({});
    setMcManualPoints({});
    setManualActiveGroup('');
    setScoringMode('equal');
    setFileName('');
    setCsvContent('');
    setError('');
    setSaveMsg('');
    setSavedSinavId(null);
    setSavedOpticResults({});
    setLoadedExamType('');
    if (inputRef.current) inputRef.current.value = '';

    if (!selectedCourseId || !activeYear) return;
    setLoadingExams(true);
    axios.get(API_ENDPOINTS.GET_COURSE_EXAMS(selectedCourseId))
      .then(res => setCourseExams(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCourseExams([]))
      .finally(() => setLoadingExams(false));
  }, [selectedCourseId, activeYear]);

  useEffect(() => {
    if (!selectedCourseId) { setPreviousSinavlar([]); return; }
    setLoadingPrevious(true);
    axios.get(API_ENDPOINTS.GET_OPTIC_SINAVLAR(selectedCourseId))
      .then(res => setPreviousSinavlar(Array.isArray(res.data) ? res.data : []))
      .catch(() => setPreviousSinavlar([]))
      .finally(() => setLoadingPrevious(false));
  }, [selectedCourseId]);

  // Seçili ders değişince öğrenci ad-soyad haritasını yükle
  useEffect(() => {
    if (!selectedCourseId) { setStudentNames({}); return; }
    axios.get(API_ENDPOINTS.GET_STUDENT_LIST(selectedCourseId))
      .then(res => {
        const list = Array.isArray(res.data?.students) ? res.data.students : [];
        const map = {};
        list.forEach(s => {
          const name = (s.student_name || '').trim();
          const key = String(s.student_no ?? '').trim();
          if (!key || !name) return;
          map[key] = name;
          const stripped = key.replace(/^0+/, '');   // baştaki sıfırları da eşleştir
          if (stripped) map[stripped] = name;
        });
        setStudentNames(map);
      })
      .catch(() => setStudentNames({}));
  }, [selectedCourseId]);

  // Öğrenci numarasından ad-soyad bul (numara formatı farklılıklarına dayanıklı)
  const nameFor = (no) => {
    const key = String(no ?? '').trim();
    if (!key) return '';
    return studentNames[key] || studentNames[key.replace(/^0+/, '')] || '';
  };

  const refreshPreviousSinavlar = () => {
    if (!selectedCourseId) return;
    axios.get(API_ENDPOINTS.GET_OPTIC_SINAVLAR(selectedCourseId))
      .then(res => setPreviousSinavlar(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  };

  const handleRowClick = (sinavId) => {
    handleClear();
    axios.get(API_ENDPOINTS.GET_OPTIC_DETAIL(sinavId))
      .then(async res => {
        const { sinav_meta, answer_keys, students } = res.data;
        const resultsResponse = await axios.get(API_ENDPOINTS.GET_OPTIC_RESULTS(sinavId))
          .catch(() => ({ data: [] }));
        const savedResults = {};
        (Array.isArray(resultsResponse.data) ? resultsResponse.data : []).forEach(result => {
          savedResults[String(result.student_no).trim()] = result;
        });
        setSavedOpticResults(savedResults);
        const CLASSIC_SLOTS = 10;
        const keyRows = Object.entries(answer_keys).map(([booklet, answers]) => [
          '0', booklet, ...Array(CLASSIC_SLOTS).fill(''), ...answers
        ]);
        const studentRows = students.map(s => {
          const klassik = s.klasik_puanlar || [];
          const pad = Array(Math.max(0, CLASSIC_SLOTS - klassik.length)).fill('');
          return [s.student_no, s.booklet, ...klassik, ...pad, ...(s.test_cevaplar || [])];
        });
        const allRows = [...keyRows, ...studentRows];
        const colCount = Math.max(...allRows.map(r => r.length), 13);
        const mkColName = (i) => {
          let name = '';
          do { name = String.fromCharCode(65 + (i % 26)) + name; i = Math.floor(i / 26) - 1; } while (i >= 0);
          return name;
        };
        const hdrs = Array.from({ length: colCount }, (_, i) => mkColName(i));
        const classicPts = {};
        const numClassicQ = {};
        Object.keys(answer_keys).forEach(g => {
          classicPts[g] = Array(CLASSIC_SLOTS).fill(0);
          numClassicQ[g] = 0;
        });
        const manualByGroup = {};
        Object.entries(answer_keys).forEach(([g, ans]) => {
          const n = ans.length;
          manualByGroup[g] = Array(n).fill(n > 0 ? parseFloat((100 / n).toFixed(2)) : 0);
        });
        setHeaders(hdrs);
        setRows(allRows);
        setAnswerKeys(answer_keys);
        setClassicPoints(classicPts);
        setNumClassicQuestions(numClassicQ);
        setMcManualPoints(manualByGroup);
        setManualActiveGroup(Object.keys(answer_keys).sort()[0] || '');
        setScoringMode('equal');
        setFileName(sinav_meta.file_name || sinav_meta.exam_type || 'Optik');
        setLoadedExamType(sinav_meta.exam_type || '');
        setSavedSinavId(sinavId);
        setActiveTab('analysis');
      })
      .catch(() => {});
  };

  const handleDeleteOptic = async (sinavId) => {
    if (!activeYear) return;
    setDeleting(true);
    try {
      await axios.delete(API_ENDPOINTS.DELETE_OPTIC(sinavId));
      setPreviousSinavlar(prev => prev.filter(s => s.id !== sinavId));
      if (savedSinavId === sinavId) setSavedSinavId(null);
    } catch {
      // sessiz hata
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  const syncManualPointsByBookletOrder = (pointsByGroup, sourceGroup, sourcePoints, orders = bookletOrders, answerKeyMap = answerKeys) => {
    const sourceOrder = orders[sourceGroup];
    if (!sourceGroup || !Array.isArray(sourceOrder) || sourceOrder.length === 0) {
      return { ...pointsByGroup, [sourceGroup]: sourcePoints };
    }

    const next = { ...pointsByGroup, [sourceGroup]: sourcePoints };
    const sourceIndexByQuestionId = new Map();
    sourceOrder.forEach((qid, idx) => sourceIndexByQuestionId.set(String(qid), idx));

    Object.keys(answerKeyMap).forEach(group => {
      if (group === sourceGroup) return;
      const groupOrder = orders[group];
      if (!Array.isArray(groupOrder) || groupOrder.length === 0) return;

      const current = pointsByGroup[group] || [];
      next[group] = Array.from({ length: answerKeyMap[group]?.length || groupOrder.length }, (_, idx) => {
        const sourceIdx = sourceIndexByQuestionId.get(String(groupOrder[idx]));
        if (sourceIdx === undefined) return current[idx] || 0;
        return sourcePoints[sourceIdx] || 0;
      });
    });

    return next;
  };

  const handleExamChange = async (examId) => {
    setSelectedExamId(examId);
    setBookletOrders({});
    setHeaders([]);
    setRows([]);
    setAnswerKeys({});
    setClassicPoints({});
    setNumClassicQuestions({});
    setMcManualPoints({});
    setManualActiveGroup('');
    setScoringMode('equal');
    setFileName('');
    setCsvContent('');
    setSavedSinavId(null);
    if (inputRef.current) inputRef.current.value = '';
    setSaveMsg('');
    if (!examId) return;
    try {
      const res = await axios.get(API_ENDPOINTS.GET_EXAM_BOOKLET_MAPPING(examId));
      const orders = res.data?.booklets || {};
      setBookletOrders(orders);
      const sourceGroup = manualActiveGroup || Object.keys(answerKeys).sort()[0] || '';
      if (sourceGroup && orders[sourceGroup]) {
        setMcManualPoints(prev =>
          syncManualPointsByBookletOrder(prev, sourceGroup, prev[sourceGroup] || [], orders)
        );
      }
    } catch {
      setBookletOrders({});
    }
  };

  const handleSaveResults = async () => {
    if (!activeYear || !selectedExamId || !selectedCourse) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const payload = {
        course_id:        selectedCourse.courseId,
        program_id:       selectedCourse.programId,
        faculty_id:       selectedCourse.facultyId,
        education_type_id: selectedCourse.educationTypeId,
        exam_id:          selectedExamId,
        exam_type:        selectedExam?.weight_type || selectedExam?.title || '',
        file_name:        fileName,
        csv_content:      csvContent,
        answer_keys:      answerKeys,
        students:         gradableStudentRows.map(row => {
          const s = getSummary(row);
          return {
            student_no:     row[0],
            booklet:        row[1],
            answers:        row.slice(ANSWER_START_COL),
            klasik_puanlar: row.slice(2, 2 + (numClassicQuestions[row[1]?.trim()] || 0)).map(v => parseFloat(v) || 0),
            correct:        s?.correct ?? 0,
            wrong:          s?.wrong ?? 0,
            empty:          s?.empty ?? 0,
            dc_percent:     s ? Math.round((s.correct / (s.total || 1)) * 100) : 0,
            klasik_toplam:  s?.classicScore ?? 0,
            mc_puan:        s?.mcScore ?? 0,
            final_score:    s?.finalScore ?? 0,
          };
        }),
      };
      const res = await axios.post(API_ENDPOINTS.SAVE_OPTICAL_RESULTS, payload);
      if (res.data?.sinav_id) setSavedSinavId(res.data.sinav_id);
      setSaveMsg(`✓ ${res.data?.saved_count ?? gradableStudentRows.length} öğrenci kaydedildi.`);
      refreshPreviousSinavlar();
    } catch (e) {
      setSaveMsg(`Hata: ${e.response?.data?.error || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const isKey = (r) => { const id = r[0]?.trim(); return id === '0' || id === '00000'; };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(l => l.trim());
    if (!lines.length) return;
    const delim = lines[0].includes(';') ? ';' : ',';
    const parsed = lines.map(l => l.split(delim).map(c => c.trim()));
    const colCount = Math.max(...parsed.map(r => r.length));
    const hdrs = Array.from({ length: colCount }, (_, i) => colName(i));

    const keys = {}, classicPts = {}, numClassicQ = {};
    parsed.forEach(row => {
      if (isKey(row)) {
        const g = row[1]?.trim();
        if (g) {
          // Cevap anahtarındaki sondaki boş sütunları kırp — gerçek soru sayısı
          // anahtarın dolu olduğu son sütuna göre belirlenir (sabit 40 değil).
          const ansCols = row.slice(ANSWER_START_COL);
          let last = ansCols.length - 1;
          while (last >= 0 && !(ansCols[last] ?? '').trim()) last--;
          keys[g] = ansCols.slice(0, last + 1);
          classicPts[g] = row.slice(2, ANSWER_START_COL).map(v => parseFloat(v) || 0);
          // Cevap anahtarında puan > 0 olan son konuma göre klasik soru sayısını belirle
          let nQ = 0;
          for (let i = 0; i < classicPts[g].length; i++) {
            if (classicPts[g][i] > 0) nQ = i + 1;
          }
          numClassicQ[g] = nQ;
        }
      }
    });

    // Her kitapçık için eşit-dağılım başlangıç puanları (manuel düzenlemenin temeli)
    const manualByGroup = {};
    Object.keys(keys).forEach(g => {
      const cMax = (classicPts[g] || []).reduce((s, v) => s + v, 0);
      const n = keys[g].length;
      const equalPt = n > 0 ? parseFloat(((100 - cMax) / n).toFixed(2)) : 0;
      manualByGroup[g] = Array(n).fill(equalPt);
    });
    const firstParsedGroup = Object.keys(keys).sort()[0] || '';
    const syncedManualByGroup = firstParsedGroup
      ? syncManualPointsByBookletOrder(manualByGroup, firstParsedGroup, manualByGroup[firstParsedGroup] || [], bookletOrders, keys)
      : manualByGroup;
    setMcManualPoints(syncedManualByGroup);
    setManualActiveGroup(Object.keys(keys).sort()[0] || '');

    const keyRows  = parsed.filter(r => isKey(r)).sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));
    const dataRows = parsed.filter(r => !isKey(r));

    setHeaders(hdrs);
    setRows([...keyRows, ...dataRows]);
    setAnswerKeys(keys);
    setClassicPoints(classicPts);
    setNumClassicQuestions(numClassicQ);
    setScoringMode('equal');
    setActiveTab('key');
  };

  const getCellResult = (row, ci) => {
    if (ci < ANSWER_START_COL) return null;
    const rowId = row[0]?.trim();
    if (rowId === '0' || rowId === '00000') return 'key';
    const g = row[1]?.trim();
    const key = answerKeys[g];
    if (!key) return null;
    const ai = ci - ANSWER_START_COL;
    if (ai >= key.length) return null;
    const sa = row[ci]?.trim() || '';
    const ca = key[ai]?.trim() || '';
    if (!sa) return 'empty';
    return sa === ca ? 'correct' : 'wrong';
  };

  const getSummary = (row) => {
    const g = row[1]?.trim();
    const key = answerKeys[g];
    if (!key) return null;
    let correct = 0, wrong = 0, empty = 0;
    const totalQ = key.length;
    for (let i = 0; i < totalQ; i++) {
      const sa = row[ANSWER_START_COL + i]?.trim() || '';
      const ca = key[i]?.trim() || '';
      if (!sa) empty++;
      else if (sa === ca) correct++;
      else wrong++;
    }
    const classicMax = (classicPoints[g] || []).reduce((s, v) => s + v, 0);
    const nClassic = numClassicQuestions[g] || 0;
    const classicScore = row.slice(2, 2 + nClassic).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    let mcScore = 0;
    if (scoringMode === 'equal') {
      mcScore = correct * ((100 - classicMax) / totalQ);
    } else {
      const pts = mcManualPoints[g] || [];
      for (let i = 0; i < totalQ; i++) {
        const sa = row[ANSWER_START_COL + i]?.trim() || '';
        if (sa && sa === key[i]?.trim()) mcScore += pts[i] || 0;
      }
    }
    return { correct, wrong, empty, total: totalQ, classicScore, mcScore: parseFloat(mcScore.toFixed(2)), finalScore: parseFloat((classicScore + mcScore).toFixed(2)) };
  };

  const handleFile = (file) => {
    if (!activeYear) return;
    setError('');
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) { setError('Lütfen geçerli bir .csv dosyası yükleyin.'); return; }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => { setCsvContent(e.target.result); parseCSV(e.target.result); };
    reader.readAsText(file, 'UTF-8');
  };

  const handleClear = () => {
    setHeaders([]); setRows([]); setAnswerKeys({}); setClassicPoints({});
    setMcManualPoints({}); setManualActiveGroup(''); setScoringMode('equal'); setNumClassicQuestions({}); setFileName(''); setCsvContent(''); setError(''); setSavedSinavId(null); setSavedOpticResults({}); setLoadedExamType(''); setSaveMsg('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleManualPt = (i, v) => {
    setMcManualPoints(prev => {
      const arr = [...(prev[manualActiveGroup] || [])];
      arr[i] = parseFloat(v) || 0;
      return syncManualPointsByBookletOrder(prev, manualActiveGroup, arr);
    });
  };

  const keyRows      = rows.filter(r => isKey(r));
  const studentRows  = rows
    .filter(r => !isKey(r))
    .sort((a, b) => {
      const aEmpty = !(a[1]?.trim());
      const bEmpty = !(b[1]?.trim());
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      return 0;
    });
  // Yalnızca en az bir satırda dolu olan sütunları göster — sondaki/aradaki
  // boş sütunlar (40'lık şablondan kalan) gizlenir.
  const visibleCols  = useMemo(() => {
    const cols = [];
    for (let ci = 0; ci < headers.length; ci++) {
      if (rows.some(r => (r[ci] ?? '').toString().trim())) cols.push(ci);
    }
    return cols;
  }, [headers, rows]);
  const hasKeys      = Object.keys(answerKeys).length > 0;
  const firstGroup   = Object.keys(answerKeys).sort()[0];
  const groups       = Object.keys(answerKeys).sort();
  const mcQCount     = answerKeys[firstGroup]?.length || 0;
  const isValidStudentNo = (v) => {
    const no = (v ?? '').toString().trim();
    return no && no !== '0' && no !== '00000';
  };
  const gradableStudentRows = Array.from(
    studentRows
      .filter(r => isValidStudentNo(r[0]) && answerKeys[r[1]?.trim()])
      .reduce((byNo, row) => byNo.set(row[0].trim(), row), new Map())
      .values()
  );
  const missingBookletRows = studentRows.filter(r => isValidStudentNo(r[0]) && !answerKeys[r[1]?.trim()]);
  const questionCountWarnings = groups
    .map(g => {
      const opticTotal = (numClassicQuestions[g] || 0) + (answerKeys[g]?.length || 0);
      const bookletTotal = bookletOrders[g]?.length || 0;
      return bookletTotal > 0 && opticTotal !== bookletTotal
        ? `${g} kitapçığı: optik ${opticTotal} soru, seçili sınav ${bookletTotal} soru`
        : null;
    })
    .filter(Boolean);
  const hasQuestionCountMismatch = questionCountWarnings.length > 0;
  const activeGroup  = manualActiveGroup || firstGroup;
  const activePoints = mcManualPoints[activeGroup] || [];
  const manualTotal  = activePoints.reduce((s, v) => s + v, 0);
  const classicMax   = (classicPoints[activeGroup] || classicPoints[firstGroup] || []).reduce((s, v) => s + v, 0);
  const mcPool       = parseFloat((100 - classicMax).toFixed(2));

  const summaryForRow = (row) => {
    const saved = savedSinavId && savedOpticResults[String(row[0] ?? '').trim()];
    if (!saved) return getSummary(row);
    const mcScore = Number(saved.mc_puan) || 0;
    const finalScore = Number(saved.toplam_puan) || 0;
    const correct = Number(saved.dc) || 0;
    const wrong = Number(saved.yc) || 0;
    const empty = Number(saved.bos) || 0;
    return {
      correct, wrong, empty, total: correct + wrong + empty,
      classicScore: parseFloat((finalScore - mcScore).toFixed(2)),
      mcScore, finalScore,
    };
  };

  const savedScores = savedSinavId ? Object.values(savedOpticResults) : [];
  const totalCorrect = savedScores.length
    ? savedScores.reduce((sum, result) => sum + (Number(result.dc) || 0), 0)
    : gradableStudentRows.reduce((sum, row) => sum + (summaryForRow(row)?.correct || 0), 0);
  const totalStudents = savedScores.length || gradableStudentRows.length;
  const avgScore = totalStudents > 0
    ? ((savedScores.length
      ? savedScores.reduce((sum, result) => sum + (Number(result.toplam_puan) || 0), 0)
      : gradableStudentRows.reduce((sum, row) => sum + (summaryForRow(row)?.finalScore || 0), 0)
    ) / totalStudents).toFixed(1)
    : 0;

  const canSave = activeYear && !savedSinavId && selectedCourse && selectedExamId && hasKeys && gradableStudentRows.length > 0;

  const handleDownload = async () => {
    if (savedSinavId) {
      const a = document.createElement('a');
      a.href = API_ENDPOINTS.DOWNLOAD_OPTIC_EXCEL(savedSinavId);
      a.download = `optik_notlar_${savedSinavId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      const students = gradableStudentRows.map(row => {
        const s = getSummary(row);
        return { student_no: row[0], toplam_puan: s?.finalScore ?? 0 };
      });
      const res = await axios.post(
        API_ENDPOINTS.GENERATE_OPTIC_EXCEL,
        { students, exam_type: 'Sınav' },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `optik_notlar_${fileName.replace('.csv', '')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="or-container">
      <h2 className="or-page-title">Optik Okuma</h2>

      {/* Ders Seçimi */}
      <div className="or-course-select-bar">
        <label className="or-course-label">Derslerim:</label>
        <SearchableDropdown
          value={selectedCourseId}
          onChange={setSelectedCourseId}
          disabled={loadingCourses}
          placeholder={loadingCourses ? 'Yükleniyor…' : 'Ders Seçin'}
          ariaLabel="Derslerim"
          options={courses.map(course => ({
            value: course.courseId,
            label: `${course.courseCode} – ${course.courseName}`,
            badge: archiveReadOnly && course.hasOptic ? 'Optik Var' : '',
          }))}
        />
      </div>
      {courseError && <p role="alert">{courseError}</p>}
      {!loadingCourses && !courseError && courses.length === 0 && (
        <p>Seçili akademik yılda hesabınıza bağlı ders bulunamadı.</p>
      )}

      {activeYear && selectedCourseId && (
        <div className="or-course-select-bar">
          <label className="or-course-label">Sınav:</label>
          <SearchableDropdown
            value={selectedExamId}
            onChange={handleExamChange}
            disabled={loadingExams || courseExams.length === 0}
            placeholder={loadingExams
              ? 'Yükleniyor…'
              : courseExams.length === 0
                ? 'Bu ders için yüz yüze sınav yok'
                : 'Optik yüklenecek sınavı seçin'}
            searchPlaceholder="Sınav adı veya türüyle ara…"
            ariaLabel="Sınavlar"
            options={courseExams.map(exam => ({
              value: String(exam.id),
              label: `${exam.weight_type ? `${exam.weight_type} - ` : ''}${exam.title} (${exam.status})`,
            }))}
          />
        </div>
      )}

      {/* Önceden Yüklenmiş Optikler */}
      {selectedCourseId && (archiveReadOnly || loadingPrevious || previousSinavlar.length > 0) && (
        <div className="or-previous-section">
          <h3 className="or-previous-title">Önceden Yüklenmiş Optikler</h3>
          {loadingPrevious ? (
            <p className="or-previous-loading">Yükleniyor…</p>
          ) : previousSinavlar.length === 0 ? (
            <p className="or-previous-loading">Bu ders için seçili akademik yılda yüklenmiş optik bulunmuyor.</p>
          ) : (
            <div className="or-table-wrap">
              <table className="or-table or-previous-table">
                <thead>
                  <tr>
                    <th className="or-th">Sınav Türü</th>
                    <th className="or-th">Dosya</th>
                    <th className="or-th">Öğrenci Sayısı</th>
                    <th className="or-th">Tarih</th>
                    <th className="or-th">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {previousSinavlar.map((s, ri) => (
                    <tr
                      key={s.id}
                      className={`${ri % 2 === 0 ? 'or-tr-even' : 'or-tr-odd'} or-tr-clickable`}
                      onClick={() => handleRowClick(s.id)}
                    >
                      <td className="or-td">{s.exam_type || '—'}</td>
                      <td className="or-td">{s.file_name || '—'}</td>
                      <td className="or-td">{s.ogrenci_sayisi}</td>
                      <td className="or-td">{s.created_at}</td>
                      <td className="or-td">
                        {!archiveReadOnly && <button
                          className="or-btn or-btn--danger or-btn--sm"
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(s.id); }}
                        >
                          Sil
                        </button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>


            </div>
          )}
        </div>
      )}

      {/* CSV Yükleme — ders ve sınav seçili, dosya yok */}
      {!archiveReadOnly && selectedCourseId && selectedExamId && !fileName && (
        <div
          className={`or-dropzone${dragging ? ' or-dropzone--drag' : ''}`}
          onClick={() => inputRef.current.click()}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
        >
          <input ref={inputRef} type="file" accept=".csv" className="or-input-hidden" onChange={e => handleFile(e.target.files[0])} />
          <div className="or-dropzone-icon">📂</div>
          <p className="or-dropzone-title">CSV dosyasını buraya sürükleyin</p>
          <p className="or-dropzone-sub">veya seçmek için tıklayın &nbsp;·&nbsp; Yalnızca .csv</p>
        </div>
      )}

      {activeYear && selectedCourseId && !selectedExamId && !fileName && !loadingExams && courseExams.length > 0 && (
        <div className="or-error">Optik yüklemek için önce sınav seçin.</div>
      )}

      {activeYear && hasKeys && hasQuestionCountMismatch && (
        <div className="or-error">
          Seçili sınav ile optik soru sayısı uyuşmuyor: {questionCountWarnings.join(' · ')}
        </div>
      )}

      {activeYear && hasKeys && missingBookletRows.length > 0 && (
        <div className="or-error">
          Kitapçık işaretlememiş öğrenci var: {missingBookletRows.map(r => r[0]).join(', ')}. Kaydetmeden önce Öğrenci Cevapları sekmesinden kitapçık seçin.
        </div>
      )}

      {/* Dosya yüklendi */}
      {fileName && (
        <div className="or-file-bar">
          <div className="or-file-bar-left">
            <span className="or-file-icon">📄</span>
            <div>
              <div className="or-file-name">{normalizeTr(fileName.split('/').pop())}</div>
              <div className="or-file-meta">
                {loadedExamType || selectedExam?.weight_type || selectedExam?.title || 'Sınav'} &nbsp;·&nbsp; {totalStudents} öğrenci &nbsp;·&nbsp; {visibleCols.length} sütun &nbsp;·&nbsp; Gruplar: <strong>{Object.keys(answerKeys).sort().join(', ') || '—'}</strong>
              </div>
            </div>
          </div>
          <div className="or-file-bar-actions">
            {hasKeys && studentRows.length > 0 && (
              <button className="or-btn or-btn--secondary" onClick={handleDownload}>
                İndir
              </button>
            )}
            {canSave && (
              <button className="or-btn or-btn--primary" onClick={handleSaveResults} disabled={saving}>
                {saving ? 'Kaydediliyor…' : 'Sınava Kaydet'}
              </button>
            )}
            <button className="or-btn or-btn--secondary" onClick={handleClear}>
              {savedSinavId ? 'Görünümü Kapat' : 'Temizle'}
            </button>
          </div>
        </div>
      )}

      {saveMsg && (
        <div className={`or-modal-msg${saveMsg.startsWith('✓') ? ' or-modal-msg--ok' : ' or-modal-msg--err'}`}>
          {saveMsg}
        </div>
      )}

      {error && <div className="or-error">{error}</div>}

      {/* İstatistik Kartları */}
      {hasKeys && studentRows.length > 0 && (
        <div className="or-stats">
          <div className="or-stat-card">
            <div className="or-stat-val">{totalStudents}</div>
            <div className="or-stat-lbl">Öğrenci</div>
          </div>
          <div className="or-stat-card">
            <div className="or-stat-val">{mcQCount}</div>
            <div className="or-stat-lbl">Çoktan Seçmeli</div>
          </div>
          <div className="or-stat-card or-stat-card--blue">
            <div className="or-stat-val">{avgScore}</div>
            <div className="or-stat-lbl">Ortalama Puan</div>
          </div>
          <div className="or-stat-card or-stat-card--green">
            <div className="or-stat-val">{totalStudents > 0 ? (totalCorrect / (totalStudents * mcQCount) * 100).toFixed(1) : 0}%</div>
            <div className="or-stat-lbl">Doğru Oranı</div>
          </div>
        </div>
      )}

      {/* İçerik */}
      {rows.length > 0 && (
        <div className="or-card">
          <div className="or-tabs">
            {hasKeys && <button className={`or-tab${activeTab === 'key' ? ' or-tab--active' : ''}`} onClick={() => setActiveTab('key')}>Cevap Anahtarı</button>}
            <button className={`or-tab${activeTab === 'students' ? ' or-tab--active' : ''}`} onClick={() => setActiveTab('students')}>Öğrenci Cevapları</button>
            {hasKeys && studentRows.length > 0 && <button className={`or-tab${activeTab === 'analysis' ? ' or-tab--active' : ''}`} onClick={() => setActiveTab('analysis')}>Analiz</button>}
          </div>

          {/* Cevap Anahtarı Tab */}
          {activeTab === 'key' && hasKeys && (
            <div className="or-tab-body">
              {activeYear && mcQCount > 0 && (
                <div className="or-scoring-panel">
                  <div className="or-scoring-header">
                    <span className="or-scoring-title">Puanlama Yöntemi</span>
                    <div className="or-mode-btns">
                      <button className={`or-mode-btn${scoringMode === 'equal' ? ' or-mode-btn--active' : ''}`} onClick={() => setScoringMode('equal')}>Eşit Dağılım</button>
                      <button className={`or-mode-btn${scoringMode === 'manual' ? ' or-mode-btn--active' : ''}`} onClick={() => setScoringMode('manual')}>Manuel</button>
                    </div>
                  </div>
                  {scoringMode === 'equal' && (
                    <p className="or-scoring-desc">
                      Klasik: <strong>{classicMax} puan</strong> &nbsp;·&nbsp; Çoktan seçmeli havuzu: <strong>{mcPool} puan</strong> &nbsp;·&nbsp; Soru başına: <strong>{mcQCount > 0 ? (mcPool / mcQCount).toFixed(2) : 0} puan</strong>
                    </p>
                  )}
                  {scoringMode === 'manual' && (
                    <div className="or-manual-grid">
                      {groups.length > 1 && (
                        <div className="or-mode-btns" style={{ marginBottom: 12 }}>
                          {groups.map(g => (
                            <button
                              key={g}
                              className={`or-mode-btn${activeGroup === g ? ' or-mode-btn--active' : ''}`}
                              onClick={() => setManualActiveGroup(g)}
                            >
                              {g} Kitapçığı
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="or-manual-total">
                        {groups.length > 1 && <strong>{activeGroup} Kitapçığı — </strong>}
                        Toplam: <span className={Math.abs(manualTotal - mcPool) < 0.01 ? 'or-total-ok' : 'or-total-warn'}>{manualTotal.toFixed(2)} / {mcPool}</span>
                      </div>
                      <div className="or-manual-inputs">
                        {activePoints.map((pt, i) => (
                          <div key={i} className="or-manual-cell">
                            <label>S{i + 1}</label>
                            <input type="number" min="0" step="1" value={pt} onChange={e => handleManualPt(i, e.target.value)} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="or-legend">
                <span className="or-legend-item or-legend--key">Cevap Anahtarı</span>
                <span className="or-legend-item or-legend--correct">Doğru</span>
                <span className="or-legend-item or-legend--wrong">Yanlış</span>
                <span className="or-legend-item or-legend--empty">Boş</span>
              </div>
              <div className="or-table-wrap">
                <table className="or-table">
                  <thead>
                    <tr>
                      <th className="or-th or-th-idx">#</th>
                      {visibleCols.map(ci => <th key={ci} className="or-th">{headers[ci]}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {keyRows.map((row, ri) => (
                      <tr key={ri} className="or-tr-key">
                        <td className="or-td or-td-idx">{ri + 1}</td>
                        {visibleCols.map(ci => (
                          <td key={ci} className="or-td or-td-key">{row[ci] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Öğrenci Cevapları Tab */}
          {activeTab === 'students' && (
            <div className="or-tab-body">
              <div className="or-legend">
                <span className="or-legend-item or-legend--correct">Doğru</span>
                <span className="or-legend-item or-legend--wrong">Yanlış</span>
                <span className="or-legend-item or-legend--empty">Boş</span>
                <span className="or-legend-item or-legend--no-booklet">Kitapçık işaretlememiş</span>
                <span className="or-legend-item or-legend--manual-booklet">Kitapçık sonradan atandı</span>
              </div>
              <div className="or-table-wrap">
                <table className="or-table">
                  <thead>
                    <tr>
                      <th className="or-th or-th-idx">#</th>
                      <th className="or-th">Ad Soyad</th>
                      {visibleCols.map(ci => <th key={ci} className="or-th">{headers[ci]}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {studentRows.map((row, ri) => (
                      <tr key={ri} className={!row[1]?.trim() ? 'or-tr-no-booklet' : manualBookletSet.has(row[0]) ? 'or-tr-manual-booklet' : (ri % 2 === 0 ? 'or-tr-even' : 'or-tr-odd')}>
                        <td className="or-td or-td-idx">{ri + 1}</td>
                        <td className="or-td or-td-name">{nameFor(row[0]) || '—'}</td>
                        {visibleCols.map(ci => {
                          const res = getCellResult(row, ci);
                          const g = row[1]?.trim();
                          const nClassic = numClassicQuestions[g] || 0;
                          const isClassicCol = ci >= 2 && ci < 2 + nClassic;
                          const rawVal = row[ci] ?? '';
                          const displayVal = isClassicCol && !rawVal.toString().trim() ? '0' : rawVal;
                          return (
                            <td key={ci} className={`or-td${res ? ` or-td-${res}` : ''}`}>
                              {activeYear && ci === 1 && !row[1]?.trim() ? (
                                <select
                                  className="or-inline-select"
                                  value=""
                                  onChange={e => {
                                    const newVal = e.target.value;
                                    if (!newVal) return;
                                    setRows(prev => prev.map(r =>
                                      r[0] === row[0]
                                        ? [...r.slice(0, 1), newVal, ...r.slice(2)]
                                        : r
                                    ));
                                    setManualBookletSet(prev => new Set([...prev, row[0]]));
                                  }}
                                >
                                  <option value="">Kitapçık Seç</option>
                                  {groups.map(g => (
                                    <option key={g} value={g}>{g}</option>
                                  ))}
                                </select>
                              ) : (
                                displayVal
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Analiz Tab */}
          {activeTab === 'analysis' && hasKeys && studentRows.length > 0 && (
            <div className="or-tab-body">
              <div className="or-table-wrap">
                <table className="or-table">
                  <thead>
                    <tr>
                      <th className="or-th or-th-idx">#</th>
                      <th className="or-th">Öğrenci No</th>
                      <th className="or-th">Ad Soyad</th>
                      <th className="or-th">Grup</th>
                      <th className="or-th">Doğru</th>
                      <th className="or-th">Yanlış</th>
                      <th className="or-th">Boş</th>
                      <th className="or-th">Toplam Soru</th>
                      <th className="or-th">Klasik Puan</th>
                      <th className="or-th">ÇS Puan</th>
                      <th className="or-th">Toplam Puan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentRows.map((row, ri) => {
                      const s = summaryForRow(row);
                      return (
                        <tr key={ri} className={ri % 2 === 0 ? 'or-tr-even' : 'or-tr-odd'}>
                          <td className="or-td or-td-idx">{ri + 1}</td>
                          <td className="or-td"><strong>{row[0]}</strong></td>
                          <td className="or-td or-td-name">{nameFor(row[0]) || '—'}</td>
                          <td className="or-td">{row[1]}</td>
                          {s ? (
                            <>
                              <td className="or-td or-td-correct-val">{s.correct}</td>
                              <td className="or-td or-td-wrong-val">{s.wrong}</td>
                              <td className="or-td or-td-empty-val">{s.empty}</td>
                              <td className="or-td">{s.total}</td>
                              <td className="or-td or-td-classic">{s.classicScore}</td>
                              <td className="or-td or-td-mc">{s.mcScore}</td>
                              <td className="or-td or-td-final">{s.finalScore}</td>
                            </>
                          ) : (
                            <td className="or-td or-td-nomatch" colSpan={7}>Grup eşleşmedi</td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Optik Silme Onay Modalı */}
      {confirmDeleteId && (
        <div className="or-modal-overlay" onClick={() => !deleting && setConfirmDeleteId(null)}>
          <div className="or-modal" onClick={e => e.stopPropagation()}>
            <h3 className="or-modal-title">Optik Kaydı Sil</h3>
            <p className="or-modal-sub">
              Bu optik ve bağlı tüm hesaplama verileri kalıcı olarak silinecek.<br />
              Devam etmek istiyor musunuz?
            </p>
            <div className="or-modal-actions">
              <button
                className="or-btn or-btn--ghost"
                onClick={() => setConfirmDeleteId(null)}
                disabled={deleting}
              >
                İptal
              </button>
              <button
                className="or-btn or-btn--danger"
                onClick={() => handleDeleteOptic(confirmDeleteId)}
                disabled={deleting}
              >
                {deleting ? 'Siliniyor…' : 'Evet, Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
